// Convert any Google Drive share URL into a directly-embeddable image URL.
//
// Google Drive file embedding works best using the lh3 CDN endpoint:
//   https://lh3.googleusercontent.com/d/<FILE_ID>=w1200
// or thumbnail endpoint:
//   https://drive.google.com/thumbnail?id=<FILE_ID>&sz=w1600
//
// IMPORTANT: Browsers MUST include `referrerPolicy="no-referrer"` on <img /> elements
// loading Google Drive links, otherwise Google blocks the request with HTTP 403 Forbidden.

export function extractDriveId(url) {
  if (!url || typeof url !== "string") return null;
  if (!/drive\.google\.com|docs\.google\.com|googleusercontent\.com\/d\//.test(url)) return null;

  // /file/d/<ID>/...
  let m = url.match(/\/file\/d\/([a-zA-Z0-9_-]{10,})/);
  if (m) return m[1];

  // /thumbnail?id=<ID>  or  /uc?id=<ID>  or  ?id=<ID>
  m = url.match(/[?&]id=([a-zA-Z0-9_-]{10,})/);
  if (m) return m[1];

  // lh3.googleusercontent.com/d/<ID>
  m = url.match(/\/d\/([a-zA-Z0-9_-]{10,})/);
  if (m) return m[1];

  return null;
}

export function driveUrlToImage(url, width = 800) {
  if (!url || typeof url !== "string") return url;
  const id = extractDriveId(url);
  if (!id) return url;
  // Use high performance, dynamically compressed Google UserContent CDN URL with requested target width
  return `https://lh3.googleusercontent.com/d/${id}=w${width}`;
}

export const DEFAULT_FALLBACK_IMAGE = "https://images.pexels.com/photos/21326994/pexels-photo-21326994.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export function normaliseImageUrl(url, width = 800) {
  if (!url || typeof url !== "string" || !url.trim()) return DEFAULT_FALLBACK_IMAGE;
  const converted = driveUrlToImage(url, width);
  return converted || DEFAULT_FALLBACK_IMAGE;
}

// Smart error handler that retries alternative Google Drive endpoints before setting default placeholder
export function handleImageError(e, originalUrl, width = 800) {
  const target = e.target;
  const currentSrc = target.src || "";
  const id = extractDriveId(originalUrl || currentSrc);

  if (id) {
    // Try endpoint fallback sequence
    if (!currentSrc.includes("thumbnail?id=")) {
      target.src = `https://drive.google.com/thumbnail?id=${id}&sz=w${width}`;
      return;
    }
    if (!currentSrc.includes("/uc?export=view")) {
      target.src = `https://drive.google.com/uc?export=view&id=${id}`;
      return;
    }
    if (!currentSrc.includes(`lh3.googleusercontent.com/d/${id}`)) {
      target.src = `https://lh3.googleusercontent.com/d/${id}=w${width}`;
      return;
    }
  }

  // Final fallback if link is completely broken
  if (target.src !== DEFAULT_FALLBACK_IMAGE) {
    target.src = DEFAULT_FALLBACK_IMAGE;
  }
}

